import React, { useState } from "react";
import { Button, Typography, Box, LinearProgress } from "@mui/material";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import { storage } from "../../../../../firebase";
import { ref, uploadBytesResumable, getDownloadURL } from "firebase/storage";

export default function BackendVideoSelector({ setVideoUrl, videoUrl, setUploadedVideoUrl }) {
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);

  const handleFileUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('video/')) {
      alert('Please select a video file');
      return;
    }

    // Validate file size (e.g., max 100MB)
    const maxSize = 100 * 1024 * 1024; // 100MB
    if (file.size > maxSize) {
      alert('File size too large. Please select a video under 100MB.');
      return;
    }

    setUploading(true);
    setUploadProgress(0);

    // Create a local URL for immediate playback
    const localUrl = URL.createObjectURL(file);
    setVideoUrl(localUrl);

    try {
      // Create Firebase storage reference
      const timestamp = Date.now();
      const fileName = `gait_analysis_videos/${timestamp}_${file.name}`;
      const storageRef = ref(storage, fileName);

      // Start upload
      const uploadTask = uploadBytesResumable(storageRef, file);

      uploadTask.on(
        'state_changed',
        (snapshot) => {
          // Track upload progress
          const progress = (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
          setUploadProgress(progress);
          console.log('Upload is ' + progress + '% done');
        },
        (error) => {
          // Handle upload error
          console.error('Upload error:', error);
          alert('Upload failed: ' + error.message);
          setUploading(false);
          setUploadProgress(0);
        },
        async () => {
          // Upload completed successfully
          try {
            const downloadURL = await getDownloadURL(uploadTask.snapshot.ref);
            console.log('File available at:', downloadURL);
            setUploadedVideoUrl(downloadURL);
            setUploading(false);
            setUploadProgress(0);
          } catch (error) {
            console.error('Error getting download URL:', error);
            alert('Failed to get video URL');
            setUploading(false);
            setUploadProgress(0);
          }
        }
      );
    } catch (error) {
      console.error('Error starting upload:', error);
      alert('Failed to start upload');
      setUploading(false);
      setUploadProgress(0);
    }
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
      <input
        accept="video/*"
        style={{ display: 'none' }}
        id="video-upload-button"
        type="file"
        onChange={handleFileUpload}
        disabled={uploading}
      />
      <label htmlFor="video-upload-button">
        <Button
          variant="outlined"
          component="span"
          startIcon={<CloudUploadIcon />}
          disabled={uploading}
          sx={{ minWidth: 200 }}
        >
          {uploading ? 'Uploading...' : 'Upload Video'}
        </Button>
      </label>

      {uploading && (
        <Box sx={{ width: '100%', maxWidth: 200 }}>
          <Typography variant="body2" sx={{ mb: 1 }}>
            Uploading to Firebase: {Math.round(uploadProgress)}%
          </Typography>
          <LinearProgress variant="determinate" value={uploadProgress} />
        </Box>
      )}

      {videoUrl && !uploading && (
        <Typography variant="body2" color="success.main">
          Video selected and ready for upload
        </Typography>
      )}
    </Box>
  );
}
